import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import GiftEditor from "components/admin/GiftEditor";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { STATUS, showTime } from "./shared";

function FirstRecharge() {
  const canEdit = useCan(PERMISSIONS.EVENT_RECHARGE);
  const [run, busy] = useRunner();
  const loaded = useFetch(() => api.get("/api/admin/event/first-recharge"), []);
  const [gift, setGift] = useState([]);

  useEffect(() => {
    if (loaded.data) setGift(loaded.data.gift || []);
  }, [loaded.data]);

  const save = () =>
    run(
      async () => {
        await api.put("/api/admin/event/first-recharge", gift);
        loaded.reload();
      },
      gift.length ? "Đã lưu quà nạp lần đầu" : "Đã tắt quà nạp lần đầu"
    );

  return (
    <PageCard
      title="Quà nạp lần đầu"
      subtitle="Gửi thư một lần cho mỗi tài khoản, ngay khi đơn nạp Pet Coin đầu tiên được ghi nhận. Để trống là tắt."
    >
      {loaded.error && <Alert severity="error">{loaded.error}</Alert>}
      <MDBox display="flex" flexDirection="column" gap={2}>
        <Alert severity={gift.length ? "success" : "warning"}>
          {gift.length
            ? `Đang bật. Đã gửi cho ${loaded.data?.sent ?? 0} tài khoản.`
            : "Đang tắt. Người nạp lúc tắt vẫn được nhận ở lần nạp đầu tiên sau khi bật."}{" "}
          Người đã nạp trước khi có tính năng này không được tính là lần đầu.
        </Alert>
        <GiftEditor
          value={gift}
          onChange={setGift}
          label="Quà gửi vào thư"
          randomNote="Quà nạp nên là skin, danh hiệu, cánh, gói tài nguyên (thiết kế 97 mục 5.6)."
        />
        {canEdit && (
          <MDBox>
            <MDButton color="success" disabled={busy || loaded.loading} onClick={save}>
              Lưu quà nạp lần đầu
            </MDButton>
          </MDBox>
        )}
      </MDBox>
    </PageCard>
  );
}

function RechargeSummary() {
  const seasons = useFetch(() => api.get("/api/admin/event/seasons"), []);
  const [seasonId, setSeasonId] = useState("");
  const summary = useFetch(
    () => (seasonId ? api.get("/api/admin/event/recharge", { seasonId }) : Promise.resolve(null)),
    [seasonId]
  );
  const list = (seasons.data || []).filter((row) => row.status !== 9);
  const season = list.find((row) => row.id === seasonId);

  return (
    <PageCard
      title="Đối soát nạp theo mùa"
      subtitle="Pet Coin nạp qua web trong khoảng giờ của mùa (theo giờ web nhận tiền), cộng theo người chơi."
    >
      {(seasons.error || summary.error) && (
        <Alert severity="error">{seasons.error || summary.error}</Alert>
      )}
      <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
        <MDInput
          select
          label="Mùa"
          value={seasonId}
          sx={{ minWidth: 320 }}
          onChange={(e) => setSeasonId(e.target.value)}
        >
          {list.map((row) => (
            <MenuItem key={row.id} value={row.id}>
              #{row.id} {row.name} · {STATUS[row.status]}
            </MenuItem>
          ))}
        </MDInput>
        {season && (
          <MDTypography variant="caption" color="text">
            {showTime(season.startAt)} → {showTime(season.endAt)}
          </MDTypography>
        )}
      </MDBox>
      {summary.data && (
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6} md={3}>
            <MDTypography variant="caption" color="text">
              Tổng Pet Coin
            </MDTypography>
            <MDTypography variant="h5">
              {Number(summary.data.totalCoin).toLocaleString("vi-VN")}
            </MDTypography>
          </Grid>
          <Grid item xs={6} md={3}>
            <MDTypography variant="caption" color="text">
              Số người nạp
            </MDTypography>
            <MDTypography variant="h5">{summary.data.players}</MDTypography>
          </Grid>
          <Grid item xs={6} md={3}>
            <MDTypography variant="caption" color="text">
              Lượt nhận mốc
            </MDTypography>
            <MDTypography variant="h5">{summary.data.claims.length}</MDTypography>
          </Grid>
        </Grid>
      )}
      {seasonId ? (
        <AdminTable
          loading={summary.loading}
          rows={summary.data?.pays || []}
          rowKey="userId"
          emptyText="Không có đơn nạp trong mùa"
          columns={[
            {
              key: "name",
              label: "Nhân vật",
              render: (row) => (
                <Link to={`/players/${row.userId}`}>{row.name || `Tài khoản ${row.userId}`}</Link>
              ),
            },
            {
              key: "coin",
              label: "Pet Coin",
              align: "right",
              render: (row) => Number(row.coin).toLocaleString("vi-VN"),
            },
            { key: "orders", label: "Số đơn", align: "right" },
            { key: "claimed", label: "Mốc đã nhận", align: "right" },
          ]}
        />
      ) : (
        <MDTypography variant="button" color="text">
          Chọn một mùa để xem.
        </MDTypography>
      )}
    </PageCard>
  );
}

function RechargePanel() {
  const canView = useCan(PERMISSIONS.EVENT_RECHARGE);
  if (!canView) {
    return (
      <Alert severity="info">
        Cần quyền &quot;event.recharge&quot; để xem phần kích nạp (có số liệu doanh thu).
      </Alert>
    );
  }
  return (
    <MDBox display="flex" flexDirection="column" gap={3}>
      <FirstRecharge />
      <RechargeSummary />
    </MDBox>
  );
}

export default RechargePanel;
